import React, { useMemo, useEffect, useState } from "react";
import { Plus, Search, Edit, Trash2, X, AlertCircle, Clock, Calendar, Save, RefreshCw } from "lucide-react";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import customParseFormat from "dayjs/plugin/customParseFormat";

import { apiFetch } from "../../../lib/apiClient";
import Input from "../../../components/ui/Input";
import Select from "../../../components/ui/Select";
import Button from "../../../components/ui/Button";
import { useAuth } from "../../../context/AuthContext";
import { canAccess } from "../../../utils/permissions";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

const TZ = "Asia/Kolkata";
const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const todayIST = () => dayjs().tz(TZ).format("YYYY-MM-DD");
const displayTimeIST = (date) => (date ? dayjs(date).tz(TZ).format("hh:mm A") : "-");
const dateToUiTimeIST = (date) => (date ? dayjs(date).tz(TZ).format("HH:mm") : "");

// ✅ Convert HH:mm (IST) to ISO by pinning to a safe reference date
const timeToISO = (timeStr) => dayjs.tz(`${todayIST()} ${timeStr}`, "YYYY-MM-DD HH:mm", TZ).toISOString();

const Slot = () => {
  const { user: currentUser } = useAuth();
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [searchTerm, setSearchTerm] = useState("");

  // modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("basic"); // basic | weekly | overrides
  const [currentSlot, setCurrentSlot] = useState(null);

  // basic form (✅ no date)
  const [formData, setFormData] = useState({
    name: "",
    startTime: "",
    endTime: "",
    capacity: "",
    isActive: true,
  });

  // weekly configs: {dayOfWeek, useDefault, capacity, isActive}
  const [weekView, setWeekView] = useState(
    Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      useDefault: true,
      capacity: "",
      isActive: true,
    }))
  );

  // overrides
  const [overrideForm, setOverrideForm] = useState({
    date: todayIST(),
    capacity: "",
    isActive: true,
    note: "",
  });
  const [overrides, setOverrides] = useState([]);

  // Friendly date check panel
  const [checkDate, setCheckDate] = useState(todayIST());
  const [checkSlots, setCheckSlots] = useState([]);
  const [checkLoading, setCheckLoading] = useState(false);

  const filteredSlots = useMemo(() => {
    const q = String(searchTerm || "").trim().toLowerCase();
    if (!q) return slots;
    return slots.filter((s) => String(s.name || "").toLowerCase().includes(q));
  }, [slots, searchTerm]);

  /* ---------------------------
     API: Fetch slots list
  --------------------------- */
  const fetchSlots = async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await apiFetch(`/api/slots?includeInactive=true`); // admin sees inactive (old) slots too
      setSlots(res?.slots || []);
    } catch (err) {
      setError(err.message || "Failed to fetch slots");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlots();
  }, []);

  /* ---------------------------
     Friendly: check availability by date
  --------------------------- */
  const fetchCheckByDate = async (dateStr) => {
    try {
      setCheckLoading(true);
      const res = await apiFetch(`/api/slots/date?date=${encodeURIComponent(dateStr)}`);
      setCheckSlots(res?.slots || []);
    } catch (e) {
      setError(e.message || "Failed to fetch availability");
    } finally {
      setCheckLoading(false);
    }
  };

  useEffect(() => {
    fetchCheckByDate(checkDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [checkDate]);

  /* ---------------------------
     Load weekly configs (from backend weekView)
  --------------------------- */
  const loadDayConfigs = async (slotId) => {
    const res = await apiFetch(`/api/slots/${slotId}/day-config`);
    const serverWeek = res?.weekView || [];
    const defaultCap = Number(res?.defaultCapacity || 0);

    const next = Array.from({ length: 7 }, (_, i) => {
      const w = serverWeek.find((x) => x.dayOfWeek === i);
      const hasConfig = !!w?.hasConfig;

      return {
        dayOfWeek: i,
        useDefault: !hasConfig, // if no config -> use default
        isActive: hasConfig ? !!w.isActive : true,
        capacity: hasConfig ? String(w.capacity ?? "") : String(defaultCap || ""),
      };
    });

    setWeekView(next);
  };

  const loadDateOverrides = async (slotId) => {
    const res = await apiFetch(`/api/slots/${slotId}/date-override?upcoming=true`);
    setOverrides(res?.overrides || []);
  };

  /* ---------------------------
     Modal open/close
  --------------------------- */
  const openModal = async (slot = null) => {
    setError(null);
    setActiveTab("basic");
    setCurrentSlot(slot);

    if (slot) {
      setFormData({
        name: slot.name || "",
        startTime: dateToUiTimeIST(slot.startTime),
        endTime: dateToUiTimeIST(slot.endTime),
        capacity: String(slot.defaultCapacity ?? slot.capacity ?? ""),
        isActive: !!slot.isActive,
      });

      try {
        setBusy(true);
        await Promise.all([loadDayConfigs(slot.id), loadDateOverrides(slot.id)]);
      } catch (e) {
        setError(e.message || "Failed to load slot configs");
      } finally {
        setBusy(false);
      }
    } else {
      setFormData({
        name: "",
        startTime: "",
        endTime: "",
        capacity: "",
        isActive: true,
      });

      setWeekView(
        Array.from({ length: 7 }, (_, i) => ({
          dayOfWeek: i,
          useDefault: true,
          capacity: "",
          isActive: true,
        }))
      );
      setOverrides([]);
    }

    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentSlot(null);
    setError(null);
    setActiveTab("basic");
  };

  /* ---------------------------
     Basic form validate + submit
  --------------------------- */
  const validateForm = () => {
    if (!formData.name) return "Slot name is required.";
    if (!formData.startTime || !formData.endTime) return "Start time & end time are required.";
    if (!formData.capacity) return "Default capacity is required.";

    const start = dayjs(timeToISO(formData.startTime));
    const end = dayjs(timeToISO(formData.endTime));

    if (!start.isValid() || !end.isValid()) return "Invalid time.";
    if (!start.isBefore(end)) return "Start time must be before end time.";
    if (Number(formData.capacity) < 1) return "Capacity must be at least 1.";

    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const msg = validateForm();
    if (msg) {
      setError(msg);
      return;
    }

    try {
      setBusy(true);
      setError(null);

      const payload = {
        name: formData.name,
        capacity: Number(formData.capacity),
        isActive: !!formData.isActive,
        startTime: timeToISO(formData.startTime),
        endTime: timeToISO(formData.endTime),
      };

      if (currentSlot) {
        await apiFetch(`/api/slots/${currentSlot.id}`, { method: "PUT", body: payload });
      } else {
        await apiFetch(`/api/slots`, { method: "POST", body: payload });
      }

      await fetchSlots();
      closeModal();
    } catch (err) {
      setError(err.message || "Failed to save slot");
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------
     Delete slot
  --------------------------- */
  const handleDelete = async (id) => {
    if (!window.confirm("Delete this slot?")) return;
    try {
      setBusy(true);
      setError(null);
      await apiFetch(`/api/slots/${id}`, { method: "DELETE" });
      await fetchSlots();
    } catch (err) {
      setError(err.message || "Failed to delete slot");
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------
     Save weekly config (friendly)
     - if useDefault => delete config
     - else upsert
  --------------------------- */
  const saveWeekConfig = async () => {
    if (!currentSlot) {
      setError("Save slot first, then configure weekly capacity.");
      return;
    }

    const toDelete = weekView.filter((d) => d.useDefault).map((d) => d.dayOfWeek);

    const toUpsert = weekView
      .filter((d) => !d.useDefault)
      .map((d) => ({
        dayOfWeek: d.dayOfWeek,
        capacity: Number(d.capacity),
        isActive: !!d.isActive,
      }))
      .filter((d) => Number.isFinite(d.capacity));

    try {
      setBusy(true);
      setError(null);

      await Promise.all(toDelete.map((dow) => apiFetch(`/api/slots/${currentSlot.id}/day-config/${dow}`, { method: "DELETE" })));

      if (toUpsert.length > 0) {
        await apiFetch(`/api/slots/${currentSlot.id}/day-config/bulk`, {
          method: "POST",
          body: { configs: toUpsert },
        });
      }

      await loadDayConfigs(currentSlot.id);
      await fetchSlots();
      await fetchCheckByDate(checkDate);
    } catch (e) {
      setError(e.message || "Failed to save weekly capacity");
    } finally {
      setBusy(false);
    }
  };

  /* ---------------------------
     Date overrides
  --------------------------- */
  const addOrUpdateOverride = async () => {
    if (!currentSlot) {
      setError("Save slot first, then add date overrides.");
      return;
    }
    if (!overrideForm.date || !overrideForm.capacity) {
      setError("Override date and capacity are required.");
      return;
    }

    try {
      setBusy(true);
      setError(null);

      await apiFetch(`/api/slots/${currentSlot.id}/date-override`, {
        method: "POST",
        body: {
          date: overrideForm.date,
          capacity: Number(overrideForm.capacity),
          isActive: !!overrideForm.isActive,
          note: overrideForm.note || null,
        },
      });

      setOverrideForm({ date: todayIST(), capacity: "", isActive: true, note: "" });
      await loadDateOverrides(currentSlot.id);
      await fetchSlots();
      await fetchCheckByDate(checkDate);
    } catch (e) {
      setError(e.message || "Failed to save override");
    } finally {
      setBusy(false);
    }
  };

  const deleteOverride = async (dateStr) => {
    if (!currentSlot) return;
    if (!window.confirm(`Remove override for ${dateStr}?`)) return;

    try {
      setBusy(true);
      setError(null);
      await apiFetch(`/api/slots/${currentSlot.id}/date-override/${encodeURIComponent(dateStr)}`, { method: "DELETE" });
      await loadDateOverrides(currentSlot.id);
      await fetchSlots();
      await fetchCheckByDate(checkDate);
    } catch (e) {
      setError(e.message || "Failed to delete override");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen">
      {/* Top quick action row */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3 mb-5">
        <div className="bg-white rounded-lg border border-gray-200 p-3.5">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Total Slots</p>
          <p className="text-xl font-semibold text-gray-900 mt-0.5">{slots.length}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3.5">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Active Slots</p>
          <p className="text-xl font-semibold text-gray-900 mt-0.5">{slots.filter((s) => s.isActive).length}</p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3.5">
          <p className="text-[11px] font-medium text-gray-500 uppercase tracking-wide">Default Capacity (sum)</p>
          <p className="text-xl font-semibold text-gray-900 mt-0.5">
            {slots.filter((s) => s.isActive).reduce((sum, s) => sum + (Number(s.defaultCapacity ?? s.capacity) || 0), 0)}
          </p>
        </div>

        <div className="bg-white rounded-lg border border-gray-200 p-3.5">
          <Button className="w-full h-full justify-center gap-1.5 text-sm" onClick={() => openModal()} disabled={!canAccess(currentUser, "canCreate")}>
            <Plus className="w-3.5 h-3.5" />
            Create New Slot
          </Button>
        </div>
      </div>

      {/* Search + Friendly date check */}
      <div className="bg-white rounded-lg border border-gray-200 p-3.5 mb-5">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
            <Input
              className="pl-8 text-xs"
              placeholder="Search slots..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <div className="flex items-end gap-2">
            <div>
              <label className="block text-[11px] font-medium text-gray-600 mb-1">Check Availability Date</label>
              <Input type="date" value={checkDate} onChange={(e) => setCheckDate(e.target.value)} className="text-xs" />
            </div>
            <Button
              variant="outline"
              onClick={() => fetchCheckByDate(checkDate)}
              disabled={checkLoading}
              className="text-xs gap-1.5"
            >
              <RefreshCw className="w-3.5 h-3.5" />
              {checkLoading ? "Checking..." : "Check"}
            </Button>
          </div>

          <span className="text-xs text-gray-600">
            Showing {filteredSlots.length} slot{filteredSlots.length !== 1 ? "s" : ""}
          </span>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="mb-5 bg-red-50 border border-red-200 rounded-lg p-3.5">
          <div className="flex items-center gap-2.5">
            <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0" />
            <div>
              <p className="text-xs font-medium text-red-800">Error</p>
              <p className="text-xs text-red-700 mt-0.5">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Friendly Availability View (date-wise) */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden mb-5">
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Calendar className="w-4 h-4 text-gray-500" />
            <div>
              <p className="text-sm font-semibold text-gray-900">Availability on {checkDate}</p>
              <p className="text-xs text-gray-500">Effective capacity after applying weekly config & date overrides</p>
            </div>
          </div>
          {checkLoading && <span className="text-xs text-gray-500">Loading...</span>}
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Slot</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Time</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Booked</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Remaining</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Rule Applied</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {(checkSlots || []).map((s) => (
                <tr key={s.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{s.name}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">
                    {s.startTimeLabel} - {s.endTimeLabel}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{s.effectiveCapacity}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{s.booked}</td>
                  <td className="px-4 py-3 text-sm text-gray-900">{s.remaining}</td>
                  <td className="px-4 py-3 text-xs text-gray-600">
                    {s.capacitySource === "date_override"
                      ? "Date Override"
                      : s.capacitySource === "day_config"
                      ? "Weekly Config"
                      : "Default"}
                  </td>
                </tr>
              ))}
              {(checkSlots || []).length === 0 && !checkLoading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-sm text-gray-500">
                    No slots found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Slots table (CRUD) */}
      <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">#</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Slot Name</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Time (IST)</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Default Capacity</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2.5 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 bg-white">
              {filteredSlots.map((slot, i) => (
                <tr key={slot.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-900">{i + 1}</td>
                  <td className="px-4 py-3 text-sm font-medium text-gray-900">{slot.name}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Clock className="w-3.5 h-3.5 text-gray-400" />
                      <span className="text-sm text-gray-900">
                        {displayTimeIST(slot.startTime)} - {displayTimeIST(slot.endTime)}
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{slot.defaultCapacity ?? slot.capacity}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                        slot.isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {slot.isActive ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1.5">
                      <Button variant="ghost" size="sm" onClick={() => canAccess(currentUser, "canEdit") && openModal(slot)} disabled={!canAccess(currentUser, "canEdit")} className="p-1.5">
                        <Edit className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => canAccess(currentUser, "canDelete") && handleDelete(slot.id)}
                        disabled={!canAccess(currentUser, "canDelete")}
                        className="p-1.5 text-gray-500 hover:text-red-600"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {filteredSlots.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-sm text-gray-500">
                    No slots.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {loading && (
          <div className="text-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900 mx-auto"></div>
            <p className="text-xs text-gray-500 mt-2">Loading slots...</p>
          </div>
        )}
      </div>

      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white w-full max-w-lg rounded-lg shadow-xl">
            <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-gray-900">{currentSlot ? "Edit Slot" : "Create Slot"}</h2>
                <p className="text-xs text-gray-500 mt-0.5">Basic details + weekly capacity + date overrides</p>
              </div>
              <button onClick={closeModal} className="text-gray-400 hover:text-gray-500">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Tabs */}
            <div className="px-4 pt-3">
              <div className="flex items-center gap-2">
                {[
                  { key: "basic", label: "Basic" },
                  { key: "weekly", label: "Weekly Capacity" },
                  { key: "overrides", label: "Date Overrides" },
                ].map((t) => (
                  <button
                    key={t.key}
                    type="button"
                    onClick={() => setActiveTab(t.key)}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium border ${
                      activeTab === t.key
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {t.label}
                  </button>
                ))}
                {busy && <span className="text-xs text-gray-500 ml-auto">Working...</span>}
              </div>
            </div>

            {/* BASIC */}
            {activeTab === "basic" && (
              <form onSubmit={handleSubmit} className="px-4 py-3 space-y-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Slot Name</label>
                  <Select
                    value={formData.name}
                    options={[
                      { value: "", label: "Select a slot name", disabled: true },
                      { value: "Morning", label: "Morning" },
                      { value: "Afternoon", label: "Afternoon" },
                      { value: "Evening", label: "Evening" },
                    ]}
                    onChange={(v) => setFormData((p) => ({ ...p, name: v }))}
                    required
                    className="text-xs"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Time Range (IST)</label>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <label className="text-[11px] text-gray-500 mb-0.5 block">Start Time</label>
                      <Input
                        type="time"
                        value={formData.startTime}
                        onChange={(e) => setFormData((p) => ({ ...p, startTime: e.target.value }))}
                        required
                        className="text-xs"
                      />
                    </div>
                    <div>
                      <label className="text-[11px] text-gray-500 mb-0.5 block">End Time</label>
                      <Input
                        type="time"
                        value={formData.endTime}
                        onChange={(e) => setFormData((p) => ({ ...p, endTime: e.target.value }))}
                        required
                        className="text-xs"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Default Capacity</label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.capacity}
                    onChange={(e) => setFormData((p) => ({ ...p, capacity: e.target.value }))}
                    required
                    className="text-xs"
                  />
                  <p className="text-[11px] text-gray-500 mt-1">
                    Used when no weekly capacity or date override is set.
                  </p>
                </div>

                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!formData.isActive}
                    onChange={(e) => setFormData((p) => ({ ...p, isActive: e.target.checked }))}
                    className="rounded border-gray-300 w-3.5 h-3.5"
                  />
                  Active slot
                </label>

                <div className="flex justify-end gap-2 pt-4 border-t border-gray-200">
                  <Button type="button" variant="outline" onClick={closeModal} className="text-xs">
                    Cancel
                  </Button>
                  <Button type="submit" disabled={busy} className="text-xs gap-1.5">
                    <Save className="w-3.5 h-3.5" />
                    {busy ? "Saving..." : currentSlot ? "Update Slot" : "Create Slot"}
                  </Button>
                </div>
              </form>
            )}

            {/* WEEKLY CAPACITY */}
            {activeTab === "weekly" && (
              <div className="px-4 py-3">
                {!currentSlot ? (
                  <div className="text-sm text-gray-700">Create the slot first, then set weekly capacity.</div>
                ) : (
                  <>
                    <p className="text-xs text-gray-600 mb-3">
                      For each day, choose <b>Use Default</b> or set a custom capacity.
                    </p>

                    <div className="space-y-2">
                      {weekView.map((d) => (
                        <div key={d.dayOfWeek} className="border border-gray-200 rounded-md p-2">
                          <div className="flex items-center justify-between">
                            <div className="text-sm font-medium text-gray-900">{DAY_NAMES[d.dayOfWeek]}</div>

                            <label className="flex items-center gap-2 text-xs text-gray-700">
                              <input
                                type="checkbox"
                                checked={!!d.useDefault}
                                onChange={(e) =>
                                  setWeekView((p) =>
                                    p.map((x) =>
                                      x.dayOfWeek === d.dayOfWeek ? { ...x, useDefault: e.target.checked } : x
                                    )
                                  )
                                }
                                className="rounded border-gray-300 w-3.5 h-3.5"
                              />
                              Use Default
                            </label>
                          </div>

                          {!d.useDefault && (
                            <div className="mt-2 grid grid-cols-2 gap-2">
                              <div>
                                <label className="text-[11px] text-gray-500 mb-1 block">Capacity</label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={d.capacity}
                                  onChange={(e) =>
                                    setWeekView((p) =>
                                      p.map((x) =>
                                        x.dayOfWeek === d.dayOfWeek ? { ...x, capacity: e.target.value } : x
                                      )
                                    )
                                  }
                                  className="text-xs"
                                />
                              </div>

                              <div className="flex items-end">
                                <label className="flex items-center gap-2 text-xs text-gray-700">
                                  <input
                                    type="checkbox"
                                    checked={!!d.isActive}
                                    onChange={(e) =>
                                      setWeekView((p) =>
                                        p.map((x) =>
                                          x.dayOfWeek === d.dayOfWeek ? { ...x, isActive: e.target.checked } : x
                                        )
                                      )
                                    }
                                    className="rounded border-gray-300 w-3.5 h-3.5"
                                  />
                                  Active
                                </label>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    <div className="flex justify-end gap-2 pt-4 border-t border-gray-200 mt-4">
                      <Button variant="outline" onClick={() => loadDayConfigs(currentSlot.id)} disabled={busy} className="text-xs">
                        Reset
                      </Button>
                      <Button onClick={saveWeekConfig} disabled={busy} className="text-xs gap-1.5">
                        <Save className="w-3.5 h-3.5" />
                        {busy ? "Saving..." : "Save Weekly Capacity"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* DATE OVERRIDES */}
            {activeTab === "overrides" && (
              <div className="px-4 py-3">
                {!currentSlot ? (
                  <div className="text-sm text-gray-700">Create the slot first, then add date overrides.</div>
                ) : (
                  <>
                    <p className="text-xs text-gray-600 mb-3">
                      Use date overrides for holidays / special days. These override weekly capacity.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-2">
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-1">Date</label>
                        <Input
                          type="date"
                          value={overrideForm.date}
                          onChange={(e) => setOverrideForm((p) => ({ ...p, date: e.target.value }))}
                          className="text-xs"
                        />
                      </div>
                      <div>
                        <label className="block text-[11px] font-medium text-gray-600 mb-1">Capacity</label>
                        <Input
                          type="number"
                          min="0"
                          value={overrideForm.capacity}
                          onChange={(e) => setOverrideForm((p) => ({ ...p, capacity: e.target.value }))}
                          className="text-xs"
                        />
                      </div>
                      <div className="flex items-end">
                        <label className="flex items-center gap-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!overrideForm.isActive}
                            onChange={(e) => setOverrideForm((p) => ({ ...p, isActive: e.target.checked }))}
                            className="rounded border-gray-300 w-3.5 h-3.5"
                          />
                          Active
                        </label>
                      </div>
                      <div className="flex items-end">
                        <Button onClick={addOrUpdateOverride} disabled={busy} className="w-full text-xs">
                          {busy ? "Saving..." : "Save"}
                        </Button>
                      </div>
                    </div>

                    <div className="mb-3">
                      <label className="block text-[11px] font-medium text-gray-600 mb-1">Note (optional)</label>
                      <Input
                        value={overrideForm.note}
                        onChange={(e) => setOverrideForm((p) => ({ ...p, note: e.target.value }))}
                        placeholder="Holiday / Extra capacity / etc..."
                        className="text-xs"
                      />
                    </div>

                    <div className="border-t border-gray-200 pt-3">
                      <div className="text-xs font-medium text-gray-900 mb-2">Upcoming Overrides</div>

                      <div className="overflow-x-auto border border-gray-200 rounded-md">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Day</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Capacity</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Active</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Note</th>
                              <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Action</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-200 bg-white">
                            {(overrides || []).map((o) => (
                              <tr key={o.dateLabel || o.date} className="hover:bg-gray-50">
                                <td className="px-3 py-2 text-sm text-gray-900">{o.dateLabel}</td>
                                <td className="px-3 py-2 text-sm text-gray-700">{o.dayName}</td>
                                <td className="px-3 py-2 text-sm text-gray-900">{o.capacity}</td>
                                <td className="px-3 py-2 text-sm text-gray-900">{o.isActive ? "Yes" : "No"}</td>
                                <td className="px-3 py-2 text-sm text-gray-700">{o.note || "-"}</td>
                                <td className="px-3 py-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteOverride(o.dateLabel)}
                                    className="text-gray-500 hover:text-red-600 p-1.5"
                                  >
                                    <Trash2 className="w-3.5 h-3.5" />
                                  </Button>
                                </td>
                              </tr>
                            ))}
                            {(overrides || []).length === 0 && (
                              <tr>
                                <td colSpan={6} className="px-3 py-4 text-sm text-gray-500">
                                  No upcoming overrides.
                                </td>
                              </tr>
                            )}
                          </tbody>
                        </table>
                      </div>

                      <div className="flex justify-end pt-3">
                        <Button variant="outline" onClick={() => loadDateOverrides(currentSlot.id)} disabled={busy} className="text-xs">
                          Refresh
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Slot;